import type * as pc from "playcanvas";

type AppWithSound = { soundManager?: { context?: AudioContext } };

/**
 * Resume the Web Audio context if it exists and is suspended. Safe to
 * call anytime (no-op when there's no context yet or it's already
 * running). Use as a belt-and-suspenders call right before a `play()`.
 */
export function resumeAudioContext(app: pc.AppBase): void {
  try {
    const ctx = (app as unknown as AppWithSound).soundManager?.context;
    if (ctx && ctx.state !== "running") void ctx.resume().catch(() => {});
  } catch {
    /* no Web Audio support — nothing to resume */
  }
}

let wired = false;

/**
 * Force-resume the audio context on the first real user gesture.
 *
 * PlayCanvas creates the AudioContext lazily and only arms its own
 * "unlock on next input" listeners the instant something first reads
 * `soundManager.context` — which happens asynchronously when an audio
 * asset finishes loading. If the player has already clicked for
 * pointer-lock by then and then only presses WASD (never clicks again),
 * that built-in unlock never fires and the context stays suspended →
 * total silence. So we resume it ourselves from the first gesture.
 *
 * Idempotent across the whole app — safe to call from every audio
 * manager; only one set of listeners is ever installed.
 */
export function unlockAudioOnGesture(app: pc.AppBase): void {
  if (wired) return;
  wired = true;

  const cleanup = (): void => {
    window.removeEventListener("pointerdown", onGesture, true);
    window.removeEventListener("keydown", onGesture, true);
    window.removeEventListener("click", onGesture, true);
  };

  const onGesture = (): void => {
    try {
      const ctx = (app as unknown as AppWithSound).soundManager?.context;
      if (!ctx) return;
      if (ctx.state === "running") {
        cleanup();
        return;
      }
      void ctx
        .resume()
        .then(() => {
          cleanup();
          console.log("[audio] context resumed:", ctx.state);
        })
        .catch(() => {});
    } catch {
      /* no Web Audio support */
    }
  };

  // Capture phase so we still see the canvas click even though it is
  // consumed by the pointer-lock handler.
  window.addEventListener("pointerdown", onGesture, true);
  window.addEventListener("keydown", onGesture, true);
  window.addEventListener("click", onGesture, true);
}
